import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend
} from "recharts";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { getSummary } from "../../services/insightsApi";
import style from "./Insights.module.css";

const SENTIMENT_COLORS = {
  positive: "#4ea448",
  negative: "#d4452f",
  neutral: "#888",
};

const CHART_PALETTE = [
  "#6366f1", "#06b6d4", "#f59e0b", "#ef4444", "#10b981",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#3b82f6",
];

const InsightsDashboard = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sentimentFilter, setSentimentFilter] = useState(null);

  useEffect(() => {
    if (!org?.payload) return;
    setLoading(true);
    getSummary(org.payload)
      .then(setSummary)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [org?.payload]);

  const sentimentData = summary
    ? [
        { name: "Positive", value: summary.sentimentCounts.positive, fill: SENTIMENT_COLORS.positive },
        { name: "Negative", value: summary.sentimentCounts.negative, fill: SENTIMENT_COLORS.negative },
        { name: "Neutral", value: summary.sentimentCounts.neutral, fill: SENTIMENT_COLORS.neutral },
      ]
    : [];

  const categoryData = summary
    ? Object.entries(summary.categories)
        .map(([id, cat]) => {
          const count = sentimentFilter && summary.categorySentiment?.[cat.name]
            ? summary.categorySentiment[cat.name][sentimentFilter] || 0
            : cat.count;
          return { name: cat.name, count };
        })
        .filter((c) => c.name !== "Rating Only" && c.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
    : [];

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const sixMonthsCutoff = sixMonthsAgo.toISOString().substring(0, 10);

  const trendData = summary
    ? Object.entries(summary.sentimentOverTime)
        .filter(([day]) => day !== "unknown" && day >= sixMonthsCutoff)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, data]) => ({
          day,
          positive: data.positive,
          negative: data.negative,
          neutral: data.neutral,
          total: data.total,
        }))
    : [];

  const total = summary?.totalRecords || 0;
  const sentimentPercent = (type) =>
    total > 0 ? Math.round((summary.sentimentCounts[type] / total) * 100) : 0;

  return (
    <div className="mainContentInner">
      <TitleBar
        title="Insights"
        actions={[
          { icon: "upload", name: "Import", action: () => navigate("/insights/import"), variants: ["text"] },
          { icon: "search", name: "Explore", action: () => navigate("/insights/explore"), variants: ["text"] },
          { icon: "forum", name: "Ask", action: () => navigate("/insights/ask"), variants: ["text"] },
        ]}
        breadcrumb={[{ name: "Home", url: "/" }]}
      />

      <div className="contentPadding">
        {loading && <div className={style.loadingState}>Loading insights...</div>}
        {error && <div className={style.errorState}>{error}</div>}

        {!loading && !error && total === 0 && (
          <div className={style.emptyState}>
            <Icon>lightbulb</Icon>
            <h2>No feedback data yet</h2>
            <p>Import customer feedback to get started with insights and analysis.</p>
            <button className={style.ctaButton} onClick={() => navigate("/insights/import")}>
              Import Feedback
            </button>
          </div>
        )}

        {!loading && !error && total > 0 && (
          <>
            <div className={style.statsRow}>
              <div className={style.statCard}>
                <div className={style.statValue}>{total}</div>
                <div className={style.statLabel}>Total Feedback</div>
              </div>
              <div className={`${style.statCard} ${style.positive}`}>
                <div className={style.statValue}>{sentimentPercent("positive")}%</div>
                <div className={style.statLabel}>Positive ({summary.sentimentCounts.positive})</div>
              </div>
              <div className={`${style.statCard} ${style.negative}`}>
                <div className={style.statValue}>{sentimentPercent("negative")}%</div>
                <div className={style.statLabel}>Negative ({summary.sentimentCounts.negative})</div>
              </div>
              <div className={`${style.statCard} ${style.neutralStat}`}>
                <div className={style.statValue}>{sentimentPercent("neutral")}%</div>
                <div className={style.statLabel}>Neutral ({summary.sentimentCounts.neutral})</div>
              </div>
            </div>

            {trendData.length > 1 && (
              <div className={style.chartSection}>
                <h3>Sentiment Over Time</h3>
                <div className={style.chartWrapper}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--on--700)" />
                      <XAxis dataKey="day" stroke="var(--on--300)" fontSize={11} tick={{ angle: -45, textAnchor: "end" }} height={60} />
                      <YAxis stroke="var(--on--300)" fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "var(--on--800)",
                          border: "1px solid var(--on--600)",
                          borderRadius: "8px",
                          color: "var(--on--100)",
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="positive" stroke={SENTIMENT_COLORS.positive} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="negative" stroke={SENTIMENT_COLORS.negative} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="neutral" stroke={SENTIMENT_COLORS.neutral} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className={style.chartsGrid}>
              <div className={style.chartSection}>
                <h3>
                  Sentiment Breakdown
                  {sentimentFilter && (
                    <button className={style.clearFilter} onClick={() => setSentimentFilter(null)}>
                      Filtering: {sentimentFilter}
                      <Icon>close</Icon>
                    </button>
                  )}
                </h3>
                <div className={style.chartWrapper}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={sentimentData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        cursor="pointer"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        onClick={(data) => {
                          const key = data.name.toLowerCase();
                          setSentimentFilter(sentimentFilter === key ? null : key);
                        }}
                      >
                        {sentimentData.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={entry.fill}
                            opacity={sentimentFilter && sentimentFilter !== entry.name.toLowerCase() ? 0.3 : 1}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={style.chartSection}>
                <h3>Top Categories{sentimentFilter ? ` (${sentimentFilter})` : ""}</h3>
                <div className={style.chartWrapper}>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={categoryData} layout="vertical" margin={{ left: 10 }}>
                      <XAxis type="number" stroke="var(--on--300)" fontSize={12} />
                      <YAxis type="category" dataKey="name" stroke="var(--on--300)" fontSize={12} width={120} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "var(--on--800)",
                          border: "1px solid var(--on--600)",
                          borderRadius: "8px",
                          color: "var(--on--100)",
                        }}
                      />
                      <Bar
                        dataKey="count"
                        radius={[0, 4, 4, 0]}
                        cursor="pointer"
                        onClick={(data) => navigate(`/insights/category/${encodeURIComponent(data.name)}`)}
                      >
                        {categoryData.map((_, i) => (
                          <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {Object.keys(summary.sources || {}).length > 0 && (
              <div className={style.sourcesSection}>
                <h3>Data Sources</h3>
                <div className={style.sourcesList}>
                  {Object.entries(summary.sources).map(([id, src]) => (
                    <div key={id} className={style.sourceItem}>
                      <Icon>{src.type === "google_play" ? "shop" : "description"}</Icon>
                      <div className={style.sourceInfo}>
                        <div className={style.sourceName}>{src.name}</div>
                        <div className={style.sourceMeta}>
                          {src.recordCount ? `${src.recordCount} records` : ""}
                          {src.lastSync ? ` \u00B7 Last synced ${new Date(src.lastSync).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsDashboard;
